import apiClient from "./api";

export interface PatientAssociation {
  entityID: number;
  externalLookup?: string;
  diabetesProgram?: number;
  weightProgram?: number;
  hypertensionProgram?: number;
  entityName?: string;
}

export interface Patient {
  id: number;
  chartID: string;
  firstname: string;
  lastname: string;
  dob: string;
  statusID: number;
  city: string;
  state: string;
  zipcode: string;
  externalLookup?: string;
  gender?: string;
  emailAddress?: string;
  phone1?: string;
  address1?: string;
  address2?: string;
  associations?: PatientAssociation[];
  isOnHold?: number;
  holdDate?: string;
  releaseDate?: string;
}

export interface PatientDetails {
  patientID: number;
  diabetic: boolean;
  diagnosis: string;
  conditions: string;
}

export interface PatientProvider {
  accountID: number;
  entityID: number;
  patientID: number;
  careProvider?: string;
  externalLookup?: string;
  externalSystemId?: string;
  statusID: number;
  managing?: boolean;
  firstname: string;
  lastname: string;
  title?: string;
  emailAddress: string;
  accountAddress1?: string;
  accountAddress2?: string;
  accountCity?: string;
  accountState?: string;
  accountZipcode?: string;
  accountPhone?: string;
  entityName?: string;
  entityTypeID?: number;
  entityType?: string;
  entityAddress1?: string;
  entityAddress2?: string;
  entityCity?: string;
  entityState?: string;
  entityZipcode?: string;
  entityPhone?: string;
  diabetesProgram?: number;
  weightProgram?: number;
  hypertensionProgram?: number;
}

export interface ProviderSearchResult {
  entityID: number;
  entityName: string;
  entityTypeID: number;
  entityType: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zipcode?: string;
  phone?: string;
  emailAddress?: string;
  primaryAccountID?: number;
  primaryFirstname?: string;
  primaryLastname?: string;
  primaryTitle?: string;
}

export interface PatientDemographics {
  id: number;
  firstname: string;
  middleName?: string;
  lastname: string;
  phone1?: string;
  gender?: string;
  dob?: string;
  country: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zipcode: string;
  sameShippingAddress: boolean;
  shippingAddress1?: string;
  shippingAddress2?: string;
  shippingCity?: string;
  shippingState?: string;
  shippingZipcode?: string;
}

export interface PatientSecurity {
  id: number;
  username: string;
  systemTypeID: number;
  securityCode?: string;
  securityQuestionID?: number;
  securityAnswer?: string;
}

export interface PatientHealth {
  patientID?: number;
  accountID?: number;
  diabetic: boolean;
  typeDiabetes: number;
  insulinDependent?: boolean;
  conditions?: string;
  glTestRate?: string;
  bpTestRate?: string;
  wsTestRate?: string;
  tmTestRate?: string;
  poTestRate?: string;
  atTestRate?: string;
  conditionsList?: Array<{ id: number; name: string }>;
  diagnosis?: Array<{ icd10Code: string; title: string; startDate: string }>;
}

export interface PatientOrder {
  id: number;
  poNumber: string;
  poDate: string;
  statusID: number;
  fromCompanyName: string;
  toCompanyName: string;
  clientCompanyName: string;
  status: string;
  total: number;
  details: Array<{
    id: number;
    chargeName: string;
    qty: number;
    charge: number;
    total: number;
  }>;
}

export interface CCMCareProvider {
  _id: string;
  fname: string;
  lname: string;
  email?: string;
  username?: string;
  isActive?: boolean;
}

export const patientService = {
  searchPatients: async (searchTerm: string, limit = 50, offset = 0) => {
    const response = await apiClient.get("/patients/search", {
      params: { q: searchTerm, limit, offset },
    });
    return response.data;
  },

  getPatientById: async (id: number): Promise<Patient> => {
    const response = await apiClient.get(`/patients/${id}`);
    return response.data;
  },

  getPatientDetails: async (id: number): Promise<PatientDetails> => {
    const response = await apiClient.get(`/patients/${id}/details`);
    return response.data;
  },

  getPatientAssociations: async (id: number) => {
    const response = await apiClient.get(`/patients/${id}/associations`);
    return response.data;
  },

  updatePatient: async (id: number, data: Partial<Patient>): Promise<void> => {
    await apiClient.put(`/patients/${id}`, data);
  },

  // Provider management
  searchProviders: async (search: string): Promise<ProviderSearchResult[]> => {
    const response = await apiClient.get(`/patients/providers/search`, {
      params: { search },
    });
    return response.data;
  },

  getPatientProviders: async (id: number): Promise<PatientProvider[]> => {
    const response = await apiClient.get(`/patients/${id}/providers`);
    return response.data;
  },

  addPatientProvider: async (
    id: number,
    provider: Partial<PatientProvider>,
  ): Promise<void> => {
    await apiClient.post(`/patients/${id}/providers`, provider);
  },

  updatePatientProvider: async (
    id: number,
    providerId: number,
    data: {
      externalLookup?: string;
      careProvider?: string;
      diabetesProgram?: number;
      weightProgram?: number;
      hypertensionProgram?: number;
    },
  ): Promise<void> => {
    await apiClient.put(`/patients/${id}/providers/${providerId}`, data);
  },

  removePatientProvider: async (
    id: number,
    providerId: number,
  ): Promise<void> => {
    await apiClient.delete(`/patients/${id}/providers/${providerId}`);
  },

  // Demographics management
  getPatientDemographics: async (id: number): Promise<PatientDemographics> => {
    const response = await apiClient.get(`/patients/${id}/demographics`);
    return response.data;
  },

  updatePatientDemographics: async (
    id: number,
    data: Partial<PatientDemographics>,
  ): Promise<void> => {
    await apiClient.put(`/patients/${id}/demographics`, data);
  },

  // Security management
  getPatientSecurity: async (id: number): Promise<PatientSecurity> => {
    const response = await apiClient.get(`/patients/${id}/security`);
    return response.data;
  },

  updatePatientSecurity: async (
    id: number,
    data: Partial<PatientSecurity>,
  ): Promise<void> => {
    await apiClient.put(`/patients/${id}/security`, data);
  },

  // Health management
  getPatientHealth: async (id: number): Promise<PatientHealth> => {
    const response = await apiClient.get(`/patients/${id}/health`);
    return response.data;
  },

  updatePatientHealth: async (
    id: number,
    data: Partial<PatientHealth>,
  ): Promise<void> => {
    await apiClient.put(`/patients/${id}/health`, data);
  },

  // Orders
  getPatientOrders: async (id: number): Promise<PatientOrder[]> => {
    const response = await apiClient.get(`/patients/${id}/orders`);
    return response.data;
  },

  // Delete patient account
  deletePatientAccount: async (
    id: number,
    confirmFirstName: string,
    confirmLastName: string,
  ): Promise<void> => {
    await apiClient.delete(`/patients/${id}`, {
      data: { confirmFirstName, confirmLastName },
    });
  },

  // CCM Care Provider Management
  getCCMCareProviders: async (entityId: number): Promise<CCMCareProvider[]> => {
    const response = await apiClient.get(
      `/patients/ccm/careproviders/${entityId}`,
    );
    return response.data.careProviders || [];
  },

  getPatientCCMCareProviders: async (
    patientId: number,
    entityId: number,
  ): Promise<CCMCareProvider[]> => {
    const response = await apiClient.get(
      `/patients/${patientId}/ccm/careproviders/${entityId}`,
    );
    return response.data.careProviders || [];
  },

  assignPatientToCCMCareProvider: async (
    patientId: number,
    entityId: number,
    careProviderIds: string[],
  ): Promise<{
    message: string;
    results: Array<{
      careProviderId: string;
      success: boolean;
      error?: string;
    }>;
  }> => {
    const response = await apiClient.post(
      `/patients/${patientId}/ccm/careproviders`,
      { entityId, careProviderIds },
    );
    return response.data;
  },

  importPatientToCCM: async (
    patientId: number,
    entityId: number,
    careProviderId?: string,
  ): Promise<{
    success: boolean;
    wasExisting?: boolean;
    ccmPatientId?: string;
    linked?: boolean;
    linkError?: string;
    partnerError?: string;
  }> => {
    const response = await apiClient.post(`/patients/${patientId}/ccm/import`, {
      entityId,
      careProviderId,
    });
    return response.data;
  },
};
